/**
 * The app's "Schedule" tab, on the web — as a table or a month calendar.
 *
 * Read-only: entering a score is the skip's job on the phone, and the office's
 * in Club Office. Nothing here writes.
 *
 * Two views because they answer different questions. The table answers "what
 * is on, in order, and what was the score"; the calendar answers "which
 * Tuesdays am I out" — which is the one people actually ask, and a list of
 * dated rows is a poor way to see it. The member's own games are marked in
 * both.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { longDate, time, type Draw, type League, type MyNextGame } from '@lcc/shared';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Loading, Pill, Status } from '../components/ui';
import { PageHeader } from '../App';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * The day a draw falls on, in the reader's own timezone.
 *
 * Not `iso.slice(0, 10)`: that is the UTC date, and a 7:30 pm Tuesday draw in
 * Ontario is 23:30 UTC — so slicing put it on the Wednesday while the table,
 * which formats the instant properly, called it Tuesday. The two views
 * disagreeing about what day your game is on is worse than having one view.
 */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

export function MySchedulePage() {
  const [view, setView] = useState<'table' | 'calendar'>('table');

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  const draws = useQuery({
    queryKey: ['draws', 'member'],
    queryFn: () => {
      const from = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
      const to = new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10);
      return api.get<{ data: Draw[] }>(`/draws?from=${from}&to=${to}`).then((r) => r.data);
    },
  });

  /* Which teams are the member's, so their own games can be marked. */
  const mine = useQuery({
    queryKey: ['my-next'],
    queryFn: () => api.get<{ data: MyNextGame[] }>('/games/mine/next').then((r) => r.data),
  });
  const myTeamIds = useMemo(
    () => new Set((mine.data ?? []).map((g) => g.myTeamId)),
    [mine.data],
  );

  const leagueName = (id: string) => leagues.data?.find((l) => l.id === id)?.name ?? 'League';

  const byDay = useMemo(() => {
    const map = new Map<string, Draw[]>();
    for (const d of draws.data ?? []) {
      const key = dayKey(d.scheduledAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [draws.data]);

  const isMine = (d: Draw) =>
    d.games.some((g) =>
      (g.homeTeamId && myTeamIds.has(g.homeTeamId)) || (g.awayTeamId && myTeamIds.has(g.awayTeamId)));

  return (
    <>
      <PageHeader
        title="Schedule"
        sub="The last week and the next three months"
        actions={
          <div className="mv-views" role="group" aria-label="View">
            <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')}>
              Table
            </button>
            <button type="button" aria-pressed={view === 'calendar'} onClick={() => setView('calendar')}>
              Calendar
            </button>
          </div>
        }
      />
      <div className="content stack">
        {draws.isLoading && <Loading what="Loading the schedule" />}
        {draws.error && <ErrorNote error={draws.error} />}
        {!draws.isLoading && !draws.error && byDay.length === 0 && (
          <Card>
            <Empty title="No draws scheduled">
              Schedules are posted before each set of games starts.
            </Empty>
          </Card>
        )}

        {byDay.length > 0 && view === 'table' && (
          <Card title="Every draw" flush>
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Draws and games, by date</caption>
                <thead>
                  <tr>
                    <th>Date</th><th>Time</th><th>League</th>
                    <th className="num">Sheet</th><th>Home</th><th>Away</th>
                    <th className="num">Score</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay.flatMap(([, items]) =>
                    items.flatMap((d) =>
                      d.games.map((g, gi) => {
                        const done = g.status === 'final' || g.status === 'forfeit';
                        const yours = (g.homeTeamId && myTeamIds.has(g.homeTeamId))
                          || (g.awayTeamId && myTeamIds.has(g.awayTeamId));
                        return (
                          <tr key={g.id} style={yours ? { background: 'rgb(var(--red-rgb) / .04)' } : undefined}>
                            {gi === 0
                              ? <td className="nowrap" rowSpan={d.games.length}>{longDate(d.scheduledAt)}</td>
                              : null}
                            {gi === 0
                              ? <td className="nowrap" rowSpan={d.games.length}>{time(d.scheduledAt)}</td>
                              : null}
                            {gi === 0
                              ? <td rowSpan={d.games.length}>{leagueName(d.leagueId)}</td>
                              : null}
                            <td className="num mono">{g.sheetNumber ?? '–'}</td>
                            <td style={{ fontWeight: g.winnerTeamId === g.homeTeamId ? 700 : 400 }}>
                              {g.homeTeamName ?? 'bye'}
                            </td>
                            <td style={{ fontWeight: g.winnerTeamId === g.awayTeamId ? 700 : 400 }}>
                              {g.awayTeamName ?? 'bye'}
                              {yours && <> <Pill tone="info">you</Pill></>}
                            </td>
                            <td className="num mono">{done ? <b>{g.homeScore}–{g.awayScore}</b> : '—'}</td>
                            {gi === 0
                              ? <td rowSpan={d.games.length}><Status value={d.status} /></td>
                              : null}
                          </tr>
                        );
                      }),
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {byDay.length > 0 && view === 'calendar' && (
          <>
            <Card>
              <div className="mv-cal__key">
                <span>
                  <b style={{ background: 'var(--red-600)' }} aria-hidden="true" />
                  Your games
                </span>
                <span>
                  <b style={{ background: 'var(--ice-ink)' }} aria-hidden="true" />
                  Other draws
                </span>
                <span className="muted">Times are shown in your own timezone.</span>
              </div>
            </Card>
            <CalendarView
              byDay={byDay}
              leagueName={leagueName}
              isMine={isMine}
            />
          </>
        )}
      </div>
    </>
  );
}

function CalendarView(
  { byDay, leagueName, isMine }:
  {
    byDay: Array<[string, Draw[]]>;
    leagueName: (id: string) => string;
    isMine: (d: Draw) => boolean;
  },
) {
  const today = new Date().toISOString().slice(0, 10);

  /* The months the draws actually fall in, in order. */
  const months = useMemo(() => {
    const seen = new Set<string>();
    for (const [day] of byDay) seen.add(day.slice(0, 7));
    return [...seen].sort();
  }, [byDay]);

  const lookup = useMemo(() => new Map(byDay), [byDay]);

  return (
    <div className="mv-cal">
      {months.map((ym) => {
        const [y, m] = ym.split('-').map(Number) as [number, number];
        const first = new Date(y, m - 1, 1);
        const days = new Date(y, m, 0).getDate();
        const lead = first.getDay();

        const cells: React.ReactNode[] = [];
        for (let i = 0; i < lead; i++) {
          cells.push(<div key={`pad-${i}`} className="mv-cal__day mv-cal__day--pad" />);
        }
        for (let day = 1; day <= days; day++) {
          const iso = `${ym}-${String(day).padStart(2, '0')}`;
          const items = lookup.get(iso) ?? [];
          cells.push(
            <div
              key={iso}
              className={`mv-cal__day${iso === today ? ' mv-cal__day--today' : ''}`}
            >
              <span className="mv-cal__num">{day}</span>
              {items.map((d) => (
                <span
                  key={d.id}
                  className={`mv-cal__ev${isMine(d) ? ' mv-cal__ev--mine' : ''}`}
                  title={`${leagueName(d.leagueId)} — draw ${d.drawNumber}`}
                >
                  {leagueName(d.leagueId)}
                </span>
              ))}
            </div>,
          );
        }

        return (
          <section key={ym} className="mv-cal__month">
            <h2 className="mv-cal__title">{MONTHS[m - 1]} {y}</h2>
            <div className="mv-cal__dow" aria-hidden="true">
              {DOW.map((n) => <span key={n}>{n}</span>)}
            </div>
            <div className="mv-cal__grid">{cells}</div>
          </section>
        );
      })}
    </div>
  );
}
